import cx from 'classnames';
import { useRouter } from 'next/router';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';

import useIsMobile from '../../../../../hooks/use-is-mobile';
import NoStock from '../../../../modules/no-stock-message';
import FlowTitleSEO from '../../../../ui/v2/flow-title';
import PageTitleSEO from '../../../../ui/v2/typography/page-title';
import PartPreview from '../../../vehicle/part-preview';
import { SearchCategoryName } from '../utils/get-category-by-slug';
import SetAlertInfo from '../../create-part-alert/index_new';

type ModelsTitleProps = {
  groups: TCategories;
  selection: { category: TCategorySelection };
  vehicle: TCarInfo;
  stock: TResultAPIStocksItem[];
  info: SearchCategoryName;
};

const ModelsTitle = ({
  groups,
  selection,
  vehicle,
  stock,
  info,
}: ModelsTitleProps) => {
  const { t } = useTranslation();
  const {
    query: { locale = 'en' },
  } = useRouter();
  const subCategoryPlural = `parts-and-categories-plural:parts.${selection?.category.category}.${info?.subCategoryEnPart}`;

  return (
    <FlowTitleSEO
      cols={[
        <PageTitleSEO
          key="title"
          title={
            selection?.category
              ? t('common:model.pageTitleWithCategory', {
                brand: vehicle.brand.name,
                usedWord:
                  info?.subCategoryType && info?.subCategoryType === 'F'
                    ? t('grammar:words.used.feminine')
                    : t('grammar:words.used.masculine'),
                plural: info?.subCategoryEnPart
                  ? t(
                    subCategoryPlural,
                    {},
                    {
                      fallback: info?.subCategory,
                    }
                  )
                  : info?.subCategory,
              })
              : t('common:model.pageTitle', {
                brand: vehicle.brand.name,
              })
          }
          categoryAndSubcategory={
            selection?.category && (
              <PartPreview groups={groups} selection={selection?.category} />
            )
          }
        />,
        stock && stock[0]?.stock ? (
          <h2 key="stock" className="max-w-[780px] text-center">
            <Trans
              i18nKey={
                selection?.category
                  ? 'common:model.selectFromModelStockWithPart'
                  : 'common:selectFromModelStock'
              }
              components={{ strong: <strong /> }}
              values={{
                brand: vehicle.brand.name,
                vehicle: vehicle.brand.name,
                count: stock[0]?.stock.toLocaleString(locale),
                yoursWord:
                  info?.subCategoryType && info?.subCategoryType === 'F'
                    ? t('grammar:pronnouns.yours.feminine')
                    : t('grammar:pronnouns.yours.masculine'),
                theWord:
                  info?.subCategoryType && info?.subCategoryType === 'F'
                    ? t('grammar:pronnouns.theSingular.feminine')
                    : t('grammar:pronnouns.theSingular.masculine'),
                plural: info?.subCategory,
              }}
            />
          </h2>
        ) : (
          <div className="-ml-4 -my-9">
            <NoStock
              vehicle={vehicle}
              selection={null}
              groups={groups}
              createAlert={false}
            />
          </div>
        ),
        (!stock || (stock && stock[0]?.stock <= 0)) && (
          <div className="max-lg:text-center lg:mt-0">
            <SetAlertInfo
              brand={vehicle.brand}
              model={vehicle.model}
              version={vehicle.version}
              part={info?.subCategoryAlertInfo || undefined}
            />
          </div>
        ),
      ]}
    />
  );
};

export default ModelsTitle;
